<template>
  <Modal :id="id" :modalTitle="modalTitle" :class="classes" :onClose="closeModal">
    <!-- Loading overlay -->
    <div v-if="isLoading" class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Cargando...</span>
      </div>
      <p class="mt-2">{{ loadingMessage }}</p>
    </div>

    <!-- Message overlay -->
    <div v-if="showMessage" :class="['alert', messageType === 'success' ? 'alert-success' : 'alert-danger', 'mb-3']">
      {{ messageContent }}
    </div>

    <!-- Filters -->
    <div class="row mb-3" v-if="!isLoading">
      <div class="col-md-3">
        <label class="form-label">Marca</label>
        <input type="text" class="form-control" v-model="filters.brand" @input="debouncedLoadProducts" placeholder="Buscar marca...">
      </div>
      <div class="col-md-3">
        <label class="form-label">Modelo</label>
        <input type="text" class="form-control" v-model="filters.model" @input="debouncedLoadProducts" placeholder="Buscar modelo...">
      </div>
      <div class="col-md-2">
        <label class="form-label">Capacidad</label>
        <input type="text" class="form-control" v-model="filters.capacity" @input="debouncedLoadProducts" placeholder="Ej: 128GB">
      </div>
      <div class="col-md-2">
        <label class="form-label">Color</label>
        <input type="text" class="form-control" v-model="filters.color" @input="debouncedLoadProducts" placeholder="Color...">
      </div>
      <div class="col-md-2">
        <label class="form-label">Stock</label>
        <select class="form-control" v-model="filters.inStock" @change="loadProducts">
          <option value="">Todos</option>
          <option value="true">Con stock</option>
          <option value="false">Sin stock</option>
        </select>
      </div>
    </div>

    <!-- Products table -->
    <div class="table-responsive" v-if="!isLoading && products.length > 0">
      <p class="mb-2 text-muted">Mostrando {{ from }}-{{ to }} de {{ totalItems }} productos</p>
      <table class="table table-hover">
        <thead class="thead-light">
          <tr>
            <th>Marca</th>
            <th>Modelo</th>
            <th>Capacidad</th>
            <th>Color</th>
            <th>Stock</th>
            <th>Cuota mensual</th>
            <th>Operaciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="product in products" :key="product.ean">
            <td>{{ product.brand }}</td>
            <td>{{ product.model }}</td>
            <td>{{ product.capacity }}</td>
            <td>{{ product.color }}</td>
            <td>
              <span v-if="product.stock > 0" class="badge badge-success">Sí ({{ product.stock }})</span>
              <span v-else class="badge badge-danger">No</span>
            </td>
            <td>{{ product.totalPrice }}€</td>
            <td>
              <button
                v-if="product.stock > 0"
                class="btn btn-sm btn-primary"
                @click="confirmReplacement(product)"
                :disabled="isProcessing"
              >
                Reemplazar
              </button>
              <span v-else class="text-muted">Sin stock</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="row mt-3" v-if="numberPages > 1">
        <div class="col-md-4">
          <div class="form-inline">
            <select v-model="itemsPerPage" class="form-control mr-2" @change="changeItemsPerPage">
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
            <label>productos por página</label>
          </div>
        </div>
        <div class="col-md-8">
          <nav>
            <ul class="pagination justify-content-end mb-0">
              <li class="page-item" :class="{ disabled: currentPage === 0 }">
                <a class="page-link" href="#" @click.prevent="selectPage(currentPage - 1)">Anterior</a>
              </li>
              <li
                v-for="page in paginationPages"
                :key="page"
                class="page-item"
                :class="{ active: currentPage === page }"
              >
                <a class="page-link" href="#" @click.prevent="selectPage(page)">{{ page + 1 }}</a>
              </li>
              <li class="page-item" :class="{ disabled: currentPage >= numberPages - 1 }">
                <a class="page-link" href="#" @click.prevent="selectPage(currentPage + 1)">Siguiente</a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <!-- No products message -->
    <div v-if="!isLoading && products.length === 0" class="alert alert-info">
      No se encontraron productos con los filtros seleccionados.
    </div>

    <!-- Confirmation dialog -->
    <div v-if="showConfirmation" class="confirmation-overlay">
      <div class="confirmation-dialog">
        <h5>Confirmar Reemplazo</h5>
        <p>¿Está seguro de que desea reemplazar el producto del contrato <strong>{{ contractId }}</strong> por:</p>
        <div class="product-info mb-3">
          <p class="mb-1"><strong>{{ selectedProduct.brand }} {{ selectedProduct.model }}</strong></p>
          <p class="mb-1">{{ selectedProduct.capacity }} - {{ selectedProduct.color }}</p>
          <p class="mb-0">Cuota: {{ selectedProduct.totalPrice }}€/mes</p>
        </div>
        <div class="d-flex justify-content-end">
          <button class="btn btn-secondary mr-2" @click="cancelConfirmation" :disabled="isProcessing">
            Cancelar
          </button>
          <button class="btn btn-primary" @click="executeReplacement" :disabled="isProcessing">
            <span v-if="isProcessing">
              <span class="spinner-border spinner-border-sm mr-1" role="status"></span>
              Procesando...
            </span>
            <span v-else>Confirmar Reemplazo</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Footer buttons -->
    <div class="mt-4 d-flex justify-content-end" v-if="!showConfirmation">
      <button class="btn btn-secondary" data-dismiss="modal" @click="closeModal">
        Cerrar
      </button>
    </div>
  </Modal>
</template>

<script>
import Modal from '../../../Shared/Modal.vue';
import axios from 'axios';
import { debounce } from 'lodash';

export default {
  name: 'ModalReplacement',
  props: ['id', 'modalTitle', 'class', 'contractId', 'contractIndex'],
  components: {
    Modal,
  },
  data() {
    return {
      classes: this.class,
      products: [],
      filters: {
        brand: '',
        model: '',
        capacity: '',
        color: '',
        inStock: 'true', // Por defecto mostrar solo con stock
      },
      itemsPerPage: 25,
      currentPage: 0,
      totalItems: 0,
      numberPages: 0,
      from: 0,
      to: 0,
      isLoading: false,
      loadingMessage: 'Cargando productos...',
      showMessage: false,
      messageType: '',
      messageContent: '',
      showConfirmation: false,
      selectedProduct: null,
      isProcessing: false,
    };
  },
  computed: {
    paginationPages() {
      const pages = [];
      const maxPages = 5;
      let start = Math.max(0, this.currentPage - Math.floor(maxPages / 2));
      let end = Math.min(this.numberPages, start + maxPages);

      if (end - start < maxPages) {
        start = Math.max(0, end - maxPages);
      }

      for (let i = start; i < end; i++) {
        pages.push(i);
      }
      return pages;
    },
  },
  created() {
    this.debouncedLoadProducts = debounce(this.loadProducts, 300);
  },
  mounted() {
    this.loadProducts();
  },
  methods: {
    loadProducts() {
      this.isLoading = true;
      this.loadingMessage = 'Cargando productos...';

      let filterString = '';

      if (this.filters.brand) {
        filterString += `brand=${encodeURIComponent(this.filters.brand)}&`;
      }
      if (this.filters.model) {
        filterString += `model=${encodeURIComponent(this.filters.model)}&`;
      }
      if (this.filters.capacity) {
        filterString += `capacity=${encodeURIComponent(this.filters.capacity)}&`;
      }
      if (this.filters.color) {
        filterString += `color=${encodeURIComponent(this.filters.color)}&`;
      }
      if (this.filters.inStock) {
        filterString += `inStock=${this.filters.inStock}&`;
      }

      filterString += `pagination.pageSize=${this.itemsPerPage}&`;
      filterString += `pagination.page=${this.currentPage + 1}`;

      axios.get(`/products/search?${filterString}`)
        .then((response) => {
          this.products = response.data.products || [];
          this.totalItems = response.data.pagination?.totalElements || 0;
          this.numberPages = this.totalItems % this.itemsPerPage === 0
            ? this.totalItems / this.itemsPerPage
            : Math.floor(this.totalItems / this.itemsPerPage) + 1;

          this.from = this.currentPage * this.itemsPerPage + 1;
          this.to = Math.min(
            (this.currentPage + 1) * this.itemsPerPage,
            this.totalItems
          );

          this.isLoading = false;
        })
        .catch((error) => {
          console.error('Error al cargar productos:', error);
          this.isLoading = false;
          this.showNotification('error', 'Error al cargar los productos. Por favor, intente nuevamente.');
        });
    },

    selectPage(page) {
      if (page >= 0 && page < this.numberPages) {
        this.currentPage = page;
        this.loadProducts();
      }
    },

    changeItemsPerPage() {
      this.currentPage = 0;
      this.loadProducts();
    },

    confirmReplacement(product) {
      this.selectedProduct = product;
      this.showConfirmation = true;
    },

    cancelConfirmation() {
      this.showConfirmation = false;
      this.selectedProduct = null;
    },

    async executeReplacement() {
      if (!this.selectedProduct || !this.contractId) {
        this.showNotification('error', 'Datos incompletos para realizar el reemplazo.');
        return;
      }

      this.isProcessing = true;
      this.loadingMessage = 'Procesando reemplazo...';

      try {
        const response = await axios.post(
          `/contract/${this.contractId}/replacement/${this.selectedProduct.ean}`
        );

        if (response.status === 204 || response.status === 200) {
          // Cerrar el diálogo de confirmación inmediatamente
          this.showConfirmation = false;
          this.selectedProduct = null;

          // Mostrar mensaje de éxito
          this.showNotification('success', 'Reemplazo solicitado correctamente. El proceso de envío se ha iniciado.');

          // Emitir evento para actualizar la lista de contratos
          this.$emit('replacementSuccess', {
            contractId: this.contractId,
            contractIndex: this.contractIndex,
            newProduct: this.selectedProduct,
          });

          // Cerrar el modal después de mostrar el mensaje
          setTimeout(() => {
            this.closeModal();
          }, 2000);
        }
      } catch (error) {
        console.error('Error al procesar el reemplazo:', error);

        let errorMsg = 'Error al procesar el reemplazo. Por favor, intente nuevamente.';

        if (error.response) {
          if (error.response.status === 400) {
            errorMsg = 'El contrato no está en un estado válido para realizar el reemplazo.';
          } else if (error.response.status === 404) {
            errorMsg = 'No se encontró el contrato o el producto especificado.';
          } else if (error.response.status === 401 || error.response.status === 403) {
            errorMsg = 'No tienes permisos para realizar esta operación.';
          } else if (error.response.data?.message) {
            errorMsg = error.response.data.message;
          }
        }

        this.showNotification('error', errorMsg);
        this.showConfirmation = false;
      } finally {
        this.isProcessing = false;
      }
    },

    showNotification(type, message) {
      this.messageType = type;
      this.messageContent = message;
      this.showMessage = true;

      setTimeout(() => {
        this.showMessage = false;
      }, 5000);
    },

    closeModal() {
      this.showConfirmation = false;
      this.selectedProduct = null;
      this.showMessage = false;
      this.$emit('closeModal');
    },
  },
};
</script>

<style lang="scss" scoped>
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.table-responsive {
  max-height: 400px;
  overflow-y: auto;
}

.thead-light th {
  position: sticky;
  top: 0;
  background-color: #f8f9fa;
  z-index: 1;
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.badge-danger {
  background-color: #dc3545;
  color: white;
}

.confirmation-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1060;
}

.confirmation-dialog {
  background: white;
  padding: 24px;
  border-radius: 8px;
  max-width: 450px;
  width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.product-info {
  background-color: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  border-left: 4px solid #cc0b7a;
}

.form-label {
  font-weight: 500;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.pagination .page-link {
  color: #cc0b7a;
}

.pagination .page-item.active .page-link {
  background-color: #cc0b7a;
  border-color: #cc0b7a;
  color: white;
}

.btn-primary {
  background-color: #cc0b7a;
  border-color: #cc0b7a;
}

.btn-primary:hover {
  background-color: #a30962;
  border-color: #a30962;
}

.btn-primary:disabled {
  background-color: #e085b3;
  border-color: #e085b3;
}
</style>
